import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArticleDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
    DeskField,
    DESK_INPUT_CLASS,
} from '../../../components/staff/articles/ArticleDesk';

function ArticleCategories({ userData }) {
    const [categoryName, setCategoryName] = useState('');
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');

    useEffect(() => {
        loadCategories();
    }, []);

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const loadCategories = async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/categories`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to load categories');
            }

            const data = await response.json();

            if (data.success) {
                setCategories(data.categories);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!categoryName.trim()) {
            toast.error('Category name is required');
            return;
        }

        try {
            setSubmitting(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/categories`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ name: categoryName })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Category created successfully');
                setCategoryName('');
                loadCategories();
            } else {
                toast.error(data.message || 'Failed to create category');
            }
        } catch (error) {
            console.error('Error creating category:', error);
            toast.error('Failed to create category');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (category) => {
        setEditingId(category.id);
        setEditingName(category.name);
    };

    const handleSaveEdit = async (categoryId) => {
        if (!editingName.trim()) {
            toast.error('Category name is required');
            return;
        }

        try {
            setSubmitting(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/categories/${categoryId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ name: editingName })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Category updated successfully');
                setEditingId(null);
                setEditingName('');
                loadCategories();
            } else {
                toast.error(data.message || 'Failed to update category');
            }
        } catch (error) {
            console.error('Error updating category:', error);
            toast.error('Failed to update category');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category?')) {
            return;
        }

        try {
            setSubmitting(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}articles/categories/${categoryId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Category deleted successfully');
                loadCategories();
            } else {
                toast.error(data.message || 'Failed to delete category');
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            toast.error('Failed to delete category');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <ArticleDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>Article Categories</DeskTitle>
                <DeskNote>
                    {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}
                </DeskNote>
            </DeskRibbon>

            <div className="grid grid-cols-1 items-start gap-4 min-[1360px]:grid-cols-[400px_minmax(0,1fr)]">
                <DeskPanel title="Create Category">
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <DeskField label="Category Name" required>
                            <input
                                type="text"
                                value={categoryName}
                                onChange={(e) => setCategoryName(e.target.value)}
                                className={DESK_INPUT_CLASS}
                                placeholder="Enter category name"
                                required
                            />
                        </DeskField>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)', backgroundColor: '#5A2929' }}
                        >
                            {submitting ? 'Creating...' : 'Create Category'}
                        </button>

                        <p className="text-[11px] text-gray-500">
                            Categories set the spotlight colour used on the carousel, so keep the names consistent.
                        </p>
                    </form>
                </DeskPanel>

                <DeskPanel title="All Categories" padding="p-0">
                    {loading ? (
                        <div className="space-y-2 p-3">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="h-10 animate-pulse bg-gray-100" />
                            ))}
                        </div>
                    ) : categories.length === 0 ? (
                        <p className="py-10 text-center text-sm uppercase text-gray-500">No categories found</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b-2 border-gray-300 bg-gray-100">
                                        <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Category Name</th>
                                        <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] sm:table-cell">Added By</th>
                                        <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] md:table-cell">Added</th>
                                        <th className="px-3 py-2 text-right font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((category) => (
                                        <tr key={category.id} className="border-b border-gray-200 transition-colors hover:bg-gray-50">
                                            <td className="px-3 py-3">
                                                {editingId === category.id ? (
                                                    <input
                                                        type="text"
                                                        value={editingName}
                                                        onChange={(e) => setEditingName(e.target.value)}
                                                        className={DESK_INPUT_CLASS}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-coal_dark">
                                                        {category.name}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="hidden px-3 py-3 text-[12px] text-gray-600 sm:table-cell">{category.addedBy}</td>
                                            <td className="hidden whitespace-nowrap px-3 py-3 text-[12px] text-gray-500 md:table-cell">
                                                {formatDate(category.addedTimestamp)}
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex justify-end gap-1">
                                                    {editingId === category.id ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleSaveEdit(category.id)}
                                                                disabled={submitting}
                                                                className="px-3 py-1 font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)', backgroundColor: '#5A2929' }}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingId(null)}
                                                                className="bg-gray-500 px-3 py-1 font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-white transition-colors hover:bg-gray-600"
                                                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleEdit(category)}
                                                                className="bg-blue-600 px-3 py-1 font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-white transition-colors hover:bg-blue-700"
                                                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(category.id)}
                                                                disabled={submitting}
                                                                className="bg-red-600 px-3 py-1 font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                                                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DeskPanel>
            </div>
        </ArticleDesk>
    );
}

export default ArticleCategories;
